import styles from "./liveSalesShowcase.module.css";

interface LiveSalesShowcaseProps {
  lang: "tr" | "en";
}

// Bilingual labels for the decorative dashboard mock. The page's indexed copy
// lives in landingPages.ts; these are supplementary visual labels.
const L = {
  tr: {
    caption: "Canlı satış panelinin örnek görünümü",
    live: "CANLI",
    revenue: "Ciro",
    volume: "Hacim",
    transactions: "İşlem",
    trend: "Saatlik satış trendi",
    importedLegend: "İçe aktarılmış",
    liveLegend: "Canlı",
    table: "Son satışlar",
    importedTag: "içe aktarılmış",
    liveTag: "canlı",
  },
  en: {
    caption: "Sample view of the live sales dashboard",
    live: "LIVE",
    revenue: "Revenue",
    volume: "Volume",
    transactions: "Transactions",
    trend: "Hourly sales trend",
    importedLegend: "Imported",
    liveLegend: "Live",
    table: "Recent sales",
    importedTag: "imported",
    liveTag: "live",
  },
};

// Sample stacked bars (imported = blue base, live = red cap), in %.
const BARS = [
  { imported: 46, live: 0 },
  { imported: 60, live: 0 },
  { imported: 52, live: 0 },
  { imported: 68, live: 0 },
  { imported: 40, live: 18 },
  { imported: 0, live: 54 },
  { imported: 0, live: 72 },
  { imported: 0, live: 38 },
];

const ROWS = [
  { time: "14:58", fuel: "Diesel", amount: "32.40 L", total: "1.428,00 ₺", live: true },
  { time: "14:55", fuel: "V/Power", amount: "21.10 L", total: "1.062,00 ₺", live: true },
  { time: "13:42", fuel: "Diesel", amount: "45.00 L", total: "1.980,00 ₺", live: false },
];

/**
 * A static, crawler-safe CSS mock of the live sales dashboard, used as the
 * "screenshot" on the live-sales landing page. No data fetching, no client-only
 * behaviour, so it pre-renders cleanly.
 */
function LiveSalesShowcase({ lang }: LiveSalesShowcaseProps): JSX.Element {
  const t = L[lang];

  return (
    <figure className={styles.showcase} aria-label={t.caption}>
      <div className={styles.window}>
        <div className={styles.toolbar}>
          <div className={styles.dots}>
            <span />
            <span />
            <span />
          </div>
          <span className={styles.liveBadge}>
            <span className={styles.liveDot} />
            {t.live}
          </span>
        </div>

        <div className={styles.body}>
          <div className={styles.kpis}>
            <div className={styles.kpi}>
              <span className={styles.kpiLabel}>{t.revenue}</span>
              <strong className={styles.kpiValue}>142.153 ₺</strong>
            </div>
            <div className={styles.kpi}>
              <span className={styles.kpiLabel}>{t.volume}</span>
              <strong className={styles.kpiValue}>2.153 L</strong>
            </div>
            <div className={styles.kpi}>
              <span className={styles.kpiLabel}>{t.transactions}</span>
              <strong className={styles.kpiValue}>147</strong>
            </div>
          </div>

          <div className={styles.chartCard}>
            <div className={styles.chartHeader}>
              <span className={styles.chartTitle}>{t.trend}</span>
              <div className={styles.legend}>
                <span className={styles.legendItem}>
                  <span className={`${styles.swatch} ${styles.swatchImported}`} />
                  {t.importedLegend}
                </span>
                <span className={styles.legendItem}>
                  <span className={`${styles.swatch} ${styles.swatchLive}`} />
                  {t.liveLegend}
                </span>
              </div>
            </div>
            <div className={styles.bars} aria-hidden="true">
              {BARS.map((b, i) => (
                <div key={i} className={styles.barTrack}>
                  {b.live > 0 && (
                    <div className={styles.barLive} style={{ height: `${b.live}%` }} />
                  )}
                  {b.imported > 0 && (
                    <div
                      className={styles.barImported}
                      style={{ height: `${b.imported}%` }}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className={styles.tableCard}>
            <div className={styles.chartTitle}>{t.table}</div>
            {ROWS.map((r, i) => (
              <div key={i} className={styles.row}>
                <span className={styles.rowTime}>
                  {r.time}
                  <span
                    className={`${styles.tag} ${
                      r.live ? styles.tagLive : styles.tagImported
                    }`}
                  >
                    {r.live ? t.liveTag : t.importedTag}
                  </span>
                </span>
                <span className={styles.rowFuel}>{r.fuel}</span>
                <span className={styles.rowAmount}>{r.amount}</span>
                <span className={styles.rowTotal}>{r.total}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      <figcaption className={styles.caption}>{t.caption}</figcaption>
    </figure>
  );
}

export default LiveSalesShowcase;
